import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./Supervisor.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

const AcceptInvitation: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const { user, loading: authLoading } = useAuthContext();
    const navigate = useNavigate();
    const [status, setStatus] = useState<"loading" | "accepting" | "success" | "error">("loading");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (authLoading) return;

        if (!user) {
            // Redirect to login with return path
            navigate(`/login`, { state: { from: `/invite/${token}` } });
            return;
        }

        if (!token) {
            setStatus("error");
            setErrorMessage("Invalid invitation link.");
            return;
        }

        const acceptInvite = async () => {
            setStatus("accepting");
            try {
                const res = await authenticatedFetch(`${apiUrl}/api/invitations/accept`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token }),
                });
                const data = await res.json();
                if (res.ok) {
                    setStatus("success");
                    (window as any).showNotification?.(
                        "Invitation accepted! You now have Premium Plus access.",
                        "success"
                    );
                    // Redirect to dashboard after a brief delay
                    setTimeout(() => navigate("/dashboard"), 2000);
                } else {
                    setStatus("error");
                    setErrorMessage(data.error || "Failed to accept invitation.");
                }
            } catch {
                setStatus("error");
                setErrorMessage("Failed to accept invitation. Please try again.");
            }
        };

        acceptInvite();
    }, [user, authLoading, token, navigate]);

    return (
        <div className={styles.pageContainer}>
            <SEO title="Accept Invitation | C-Shaped" />
            <Header />
            <main className={styles.mainContent}>
                <div className={styles.acceptContainer}>
                    <div className={styles.acceptCard}>
                        {status === "loading" || status === "accepting" ? (
                            <>
                                <div className={styles.loadingSpinner} style={{ margin: "0 auto 1rem" }} />
                                <h1>Accepting Invitation</h1>
                                <p>Please wait while we process your invitation...</p>
                            </>
                        ) : status === "success" ? (
                            <>
                                <h1>Invitation Accepted!</h1>
                                <p>
                                    You've been successfully linked to your supervisor and granted Premium Plus access.
                                    Redirecting to your dashboard...
                                </p>
                            </>
                        ) : (
                            <>
                                <h1>Invitation Error</h1>
                                <p className={styles.errorText}>{errorMessage}</p>
                                <div style={{ marginTop: "1.5rem" }}>
                                    <button
                                        className={styles.primaryButton}
                                        onClick={() => navigate("/dashboard")}
                                    >
                                        Go to Dashboard
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default AcceptInvitation;
